import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  LinearProgress,
  Alert,
  tableCellClasses,
  styled,
  Collapse,
  Tooltip,
  Checkbox,
  List,
  ListItem,
  ListItemText,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Menu,
  MenuItem
} from '@mui/material';
import { useDropzone } from 'react-dropzone';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DeleteIcon from '@mui/icons-material/Delete';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import AnalyticsIcon from '@mui/icons-material/Analytics';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import DownloadIcon from '@mui/icons-material/Download';
import Navbar from '../components/Navbar';
import Chatbot from '../components/Chatbot';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import * as XLSX from 'xlsx';
import { useMsal } from '@azure/msal-react';

const API_BASE_URL = process.env.REACT_APP_API_URL;

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: 'rgba(220, 20, 60, 0.08)',
    color: '#dc143c',
    fontWeight: 600,
    fontSize: '0.875rem',
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: '0.875rem',
    color: '#3a3532',
  },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  '&:nth-of-type(odd)': {
    backgroundColor: 'rgba(255, 255, 255, 0.5)',
  },
  '&:hover': {
    backgroundColor: 'rgba(220, 20, 60, 0.04)',
  },
  '& td': {
    borderColor: 'rgba(220, 20, 60, 0.1)',
  },
  '&.Mui-selected': {
    backgroundColor: 'rgba(220, 20, 60, 0.08)',
    '&:hover': {
      backgroundColor: 'rgba(220, 20, 60, 0.12)',
    }
  }
}));

const DropzoneBox = styled(Box)(({ theme }) => ({
  border: '2px dashed rgba(220, 20, 60, 0.4)',
  borderRadius: theme.shape.borderRadius,
  padding: theme.spacing(4),
  textAlign: 'center',
  backgroundColor: 'rgba(255, 255, 255, 0.8)',
  backdropFilter: 'blur(10px)',
  transition: 'all 0.3s ease',
  cursor: 'pointer',
  '&:hover': {
    borderColor: '#dc143c',
    backgroundColor: 'rgba(255, 255, 255, 0.9)',
    transform: 'translateY(-2px)',
  },
}));

const formatScore = (score) => {
  if (score === 'N/A') return 'N/A';
  const scoreNum = parseFloat(score);
  if (isNaN(scoreNum)) return 'N/A';
  return `${(scoreNum * 100).toFixed(1)}%`;
};

const getScoreColor = (score) => {
  if (score === 'N/A') return { bg: 'rgba(0, 0, 0, 0.1)', text: '#666666' };
  const scoreNum = parseFloat(score);
  if (isNaN(scoreNum)) return { bg: 'rgba(0, 0, 0, 0.1)', text: '#666666' };

  if (scoreNum >= 0.85) {
    return { bg: 'rgba(46, 125, 50, 0.2)', text: '#2e7d32' };
  } else if (scoreNum >= 0.70) {
    return { bg: 'rgba(46, 125, 50, 0.1)', text: '#2e7d32' };
  } else if (scoreNum >= 0.55) {
    return { bg: 'rgba(237, 108, 2, 0.15)', text: '#ed6c02' };
  } else if (scoreNum >= 0.40) {
    return { bg: 'rgba(237, 108, 2, 0.1)', text: '#ed6c02' };
  } else {
    return { bg: 'rgba(211, 47, 47, 0.1)', text: '#d32f2f' };
  }
};

const getScoreText = (score) => {
  if (score === 'N/A') return "No score available";
  const scoreNum = parseFloat(score);
  if (isNaN(scoreNum)) return "Invalid score";
  
  if (scoreNum >= 0.85) return "Excellent match for role and skills";
  if (scoreNum >= 0.70) return "Strong match with required qualifications";
  if (scoreNum >= 0.55) return "Good match with some gaps";
  if (scoreNum >= 0.40) return "Partial match or similar role";
  return "Limited match or different role type";
};

const triggerDownload = (content, filename, mimeType) => {
  const blob = new Blob([content], { type: mimeType });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

// --- Cell Components ---
const SkillCell = ({ text, title = "Skills" }) => {
  const [open, setOpen] = useState(false);
  const skillsArray = text ? text.split(",").map((s) => s.trim()) : [];
  const preview = skillsArray.slice(0, 3).join(", ");

  return (
    <Box sx={{ maxWidth: 250, whiteSpace: 'pre-wrap' }}>
      {preview}
      {skillsArray.length > 3 && (
        <>
          <Typography
            component="span"
            onClick={(e) => {
              e.stopPropagation();
              setOpen(true);
            }}
            sx={{
              color: '#1976d2',
              fontSize: '0.875rem',
              ml: 1,
              cursor: 'pointer',
              '&:hover': { textDecoration: 'underline' }
            }}
          >
            ...Show more
          </Typography>

          <Dialog 
            open={open} 
            onClose={(e) => {
              e.stopPropagation();
              setOpen(false);
            }}
            onClick={(e) => e.stopPropagation()}
            maxWidth="sm"
            fullWidth
          >
            <DialogTitle sx={{ color: '#3a3532', fontWeight: 600 }}>{title}</DialogTitle>
            <DialogContent dividers>
              <Typography variant="body2" sx={{ mb: 1, color: '#666' }}>
                Complete list of {title.toLowerCase()}:
              </Typography>
              <Box sx={{ p: 2, bgcolor: 'rgba(0, 0, 0, 0.04)', borderRadius: 1, maxHeight: 250, overflowY: 'auto' }}>
                <Typography variant="body2" sx={{ lineHeight: 1.6, color: '#3a3532' }}>
                  {skillsArray.join(", ")}
                </Typography>
              </Box>
            </DialogContent>
            <DialogActions>
              <Button onClick={(e) => { e.stopPropagation(); setOpen(false); }} sx={{ color: '#dc143c' }}>
                Close
              </Button>
            </DialogActions>
          </Dialog>
        </>
      )}
    </Box>
  );
};

const EducationCell = ({ text, title = "Education" }) => {
  const [open, setOpen] = useState(false);
  if (!text) return 'N/A';

  const maxLength = 60; 
  const isLong = text.length > maxLength;
  const preview = isLong ? `${text.substring(0, maxLength)}...` : text;

  return (
    <Box sx={{ maxWidth: 250, whiteSpace: 'normal' }}>
      <Typography variant="body2" sx={{ display: 'inline', color: '#3a3532', fontSize: '0.875rem' }}>
        {preview}
      </Typography>
      {isLong && (
        <>
          <Typography
            component="span"
            onClick={(e) => { e.stopPropagation(); setOpen(true); }}
            sx={{ color: '#1976d2', fontSize: '0.875rem', ml: 1, cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}
          >
            Show more
          </Typography>

          <Dialog 
            open={open} 
            onClose={(e) => { e.stopPropagation(); setOpen(false); }}
            onClick={(e) => e.stopPropagation()}
            maxWidth="sm"
            fullWidth
          >
            <DialogTitle sx={{ color: '#3a3532', fontWeight: 600 }}>{title}</DialogTitle>
            <DialogContent dividers>
              <Box sx={{ p: 2, bgcolor: 'rgba(0, 0, 0, 0.04)', borderRadius: 1, maxHeight: 250, overflowY: 'auto' }}>
                <Typography variant="body2" sx={{ lineHeight: 1.6, color: '#3a3532', whiteSpace: 'pre-wrap' }}>
                  {text}
                </Typography>
              </Box>
            </DialogContent>
            <DialogActions>
              <Button onClick={(e) => { e.stopPropagation(); setOpen(false); }} sx={{ color: '#dc143c' }}>
                Close
              </Button>
            </DialogActions>
          </Dialog>
        </>
      )}
    </Box>
  );
};

const UploadWizard = () => {
  const { accounts } = useMsal();
  const [resumes, setResumes] = useState([]);
  const [jd, setJd] = useState(null);
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [showUpload, setShowUpload] = useState(true);
  const [showResumeList, setShowResumeList] = useState(false);
  const [selectedProfiles, setSelectedProfiles] = useState([]);
  
  // State for Export Menus
  const [anchorElExportSelected, setAnchorElExportSelected] = useState(null);
  const [anchorElExportAll, setAnchorElExportAll] = useState(null);

  const { getRootProps: getResumeRootProps, getInputProps: getResumeInputProps } = useDropzone({
    accept: { 
      'application/pdf': ['.pdf'],
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx']
    },
    onDrop: acceptedFiles => {
      setResumes(prev => [...prev, ...acceptedFiles]);
      setShowResumeList(true);
      setError(null);
    }
  });

  const { getRootProps: getJdRootProps, getInputProps: getJdInputProps } = useDropzone({
    accept: { 
      'application/pdf': ['.pdf'],
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx']
    },
    maxFiles: 1,
    onDrop: acceptedFiles => {
      setJd(acceptedFiles[0]);
      setError(null);
    }
  });

  const handleRemoveResume = (indexToRemove) => {
    setResumes((prevFiles) => {
      const updatedFiles = prevFiles.filter((_, index) => index !== indexToRemove);
      if (updatedFiles.length === 0) setShowResumeList(false);
      return updatedFiles;
    });
  };

  const handleUpload = async () => {
    setLoading(true);
    setError(null);
    setUploadProgress(0);

    const activeUser = accounts.length > 0 ? accounts[0].username : "Anonymous";

    try {
      const resumeForm = new FormData();
      resumes.forEach(file => resumeForm.append('file', file));
      const resumeResponse = await fetch(`${API_BASE_URL}/uploadpdf`, {
        method: 'POST',
        body: resumeForm,
        credentials: 'include',
        headers: {
          'user-id': activeUser,
          'X-User-Email': activeUser
        }
      });

      if (!resumeResponse.ok) throw new Error('Failed to upload resumes');
      setUploadProgress(50);

      if (jd) {
        const jdForm = new FormData();
        jdForm.append('file', jd);
        const jdResponse = await fetch(`${API_BASE_URL}/uploadjd`, {
          method: 'POST',
          body: jdForm,
          credentials: 'include',
          headers: {
            'user-id': activeUser,
            'X-User-Email': activeUser
          }
        });
        if (!jdResponse.ok) throw new Error('Failed to upload JD');
      }
      setUploadProgress(75);

      const processResponse = await fetch(`${API_BASE_URL}/extract`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'user-id': activeUser,
          'X-User-Email': activeUser
        }
      });

      if (!processResponse.ok) throw new Error('Failed to process files');
      const data = await processResponse.json();
      setResults(data);
      setSelectedProfiles([]);
      setShowUpload(false);
      setUploadProgress(100);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const clearFiles = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/clear`, {
        method: 'POST',
        credentials: 'include'
      });

      if (!response.ok) {
        if (response.status === 400) {
          const data = await response.json();
          if (data.error && data.error === "No files uploaded for this session") {
            setError("No files uploaded");
          } else {
            setError(data.error || 'Failed to clear files');
          }
        } else {
          const data = await response.json().catch(() => ({}));
          setError(data.error || 'Failed to clear files');
        }
        return;
      }

      setResumes([]);
      setJd(null);
      setResults([]);
      setSelectedProfiles([]);
      setError(null);
      setUploadProgress(0);
      setShowUpload(true);
      setShowResumeList(false);
    } catch (err) {
      setError('Failed to clear files: ' + err.message);
    }
  };

  const handleSelectAllClick = (event) => {
    if (event.target.checked) {
      const newSelecteds = results.map((_, index) => index);
      setSelectedProfiles(newSelecteds);
      return;
    }
    setSelectedProfiles([]);
  };

  const handleClick = (event, index) => {
    event.stopPropagation();
    const selectedIndex = selectedProfiles.indexOf(index);
    let newSelected = [];

    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selectedProfiles, index);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selectedProfiles.slice(1));
    } else if (selectedIndex === selectedProfiles.length - 1) {
      newSelected = newSelected.concat(selectedProfiles.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(
        selectedProfiles.slice(0, selectedIndex),
        selectedProfiles.slice(selectedIndex + 1),
      );
    }
    setSelectedProfiles(newSelected);
  };

  const isSelected = (index) => selectedProfiles.indexOf(index) !== -1;

  const getExportFilename = (type, extension = 'pdf') => {
    let baseName = 'Candidates'; 
    if (jd && jd.name) {
      baseName = jd.name.replace(/\.[^/.]+$/, "");
      baseName = baseName.replace(/\s+/g, '_');
    }
    return `ResumeX_${baseName}_${type}.${extension}`;
  };

  // --- Export Generators ---
  const generatePDF = (dataToExport, fileName) => {
    const doc = new jsPDF('landscape');
    doc.setFontSize(20);
    doc.setTextColor(220, 20, 60); 
    doc.text('ResumeX Candidate Matching Report', 14, 22);
    
    const tableColumn = ["Candidate Name", "Email", "Job Role", "Education", "Skills", "Experience", "Match Score"];
    const tableRows = dataToExport.map(profile => [
      profile['Candidate Name'] || 'N/A',
      profile['Email'] || 'N/A',
      profile['Job Role'] || 'N/A',
      profile['Education'] || 'N/A',
      profile['Skills'] || 'N/A', 
      profile['Years of Experience'] || 'N/A',
      formatScore(profile.similarity_score)
    ]);

    autoTable(doc, {
      startY: 35,
      head: [tableColumn],
      body: tableRows,
      theme: 'grid',
      headStyles: { fillColor: [220, 20, 60] },
      styles: { fontSize: 9 }
    });

    doc.save(fileName);
  };

  const generateExcel = (dataToExport, fileName) => {
    const formattedData = dataToExport.map(row => ({
      "Candidate Name": row['Candidate Name'] || 'N/A',
      "Email": row['Email'] || 'N/A',
      "Job Role": row['Job Role'] || 'N/A',
      "Education": row['Education'] || 'N/A',
      "Skills": row['Skills'] || 'N/A',
      "Experience": row['Years of Experience'] || 'N/A',
      "Match Score": formatScore(row.similarity_score)
    }));

    const worksheet = XLSX.utils.json_to_sheet(formattedData);

    const columnWidths = [
      { wch: 25 }, // Name
      { wch: 30 }, // Email
      { wch: 25 }, // Job Role
      { wch: 40 }, // Education
      { wch: 50 }, // Skills
      { wch: 15 }, // Experience
      { wch: 15 }  // Score
    ];
    worksheet['!cols'] = columnWidths;

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Candidates");
    XLSX.writeFile(workbook, fileName);
  };

  const generateCSV = (dataToExport, fileName) => {
    const headers = ["Candidate Name", "Email", "Job Role", "Education", "Skills", "Experience", "Match Score"];
    const csvRows = [headers.join(",")];

    dataToExport.forEach(row => {
      const values = [
        `"${(row['Candidate Name'] || 'N/A').replace(/"/g, '""')}"`,
        `"${(row['Email'] || 'N/A').replace(/"/g, '""')}"`,
        `"${(row['Job Role'] || 'N/A').replace(/"/g, '""')}"`,
        `"${(row['Education'] || 'N/A').replace(/"/g, '""')}"`,
        `"${(row['Skills'] || 'N/A').replace(/"/g, '""')}"`,
        `"${(String(row['Years of Experience']) || 'N/A').replace(/"/g, '""')}"`,
        `"${formatScore(row.similarity_score)}"`
      ];
      csvRows.push(values.join(","));
    });

    triggerDownload(csvRows.join("\n"), fileName, "text/csv;charset=utf-8;");
  };

  const handleExport = (format, isSelectedOnly) => {
    const dataToExport = isSelectedOnly 
      ? results.filter((_, i) => selectedProfiles.includes(i)) 
      : results;
    
    let extension = format;
    if (format === 'excel') extension = 'xlsx';
    
    const suffix = isSelectedOnly ? 'Selected' : 'All';
    const fileName = getExportFilename(suffix, extension);

    if (format === 'pdf') generatePDF(dataToExport, fileName);
    if (format === 'csv') generateCSV(dataToExport, fileName);
    if (format === 'excel') generateExcel(dataToExport, fileName);
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
      <Navbar 
        hasResults={results.length > 0}
        onClearResults={clearFiles}
      />
      <Box sx={{ maxWidth: 1200, mx: 'auto', p: 3 }}>
        <Collapse in={showUpload}>
          <Paper 
            elevation={0} 
            sx={{ 
              p: 4, 
              mb: 4,
              background: 'rgba(255, 255, 255, 0.8)',
              backdropFilter: 'blur(10px)',
              borderRadius: 4,
              border: '1px solid rgba(220, 20, 60, 0.1)',
              boxShadow: '0 8px 32px rgba(220, 20, 60, 0.1)',
            }}
          >
            <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, color: '#3a3532' }}>
              Upload Files
            </Typography>

            <Box sx={{ display: 'flex', gap: 2, mb: 4 }}>
              <Box sx={{ flex: 1 }}>
                <Typography variant="subtitle1" gutterBottom sx={{ color: '#3a3532' }}>
                  Upload Resumes (PDF)
                </Typography>
                <DropzoneBox {...getResumeRootProps()}>
                  <input {...getResumeInputProps()} />
                  <CloudUploadIcon sx={{ fontSize: 40, color: '#dc143c', mb: 2 }} />
                  <Typography sx={{ color: '#3a3532' }}>
                    {resumes.length > 0
                      ? 'Drag & drop more resumes or click to add'
                      : 'Drag & drop resumes here (PDF or DOCX), or click to select'}
                  </Typography>
                </DropzoneBox>

                {resumes.length > 0 && (
                  <Box sx={{ mt: 2 }}>
                    <Button
                      fullWidth
                      onClick={() => setShowResumeList(!showResumeList)}
                      endIcon={showResumeList ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                      sx={{
                        justifyContent: 'space-between',
                        color: '#3a3532',
                        backgroundColor: 'rgba(220, 20, 60, 0.04)',
                        textTransform: 'none',
                        fontWeight: 600,
                        '&:hover': { backgroundColor: 'rgba(220, 20, 60, 0.08)' }
                      }}
                    >
                      Show Uploaded Resumes ({resumes.length})
                    </Button>
                    <Collapse in={showResumeList}>
                      <List 
                        sx={{ 
                          maxHeight: 220, 
                          overflow: 'auto', 
                          border: '1px solid rgba(220, 20, 60, 0.1)', 
                          borderRadius: 1, 
                          mt: 1,
                          bgcolor: 'rgba(255, 255, 255, 0.6)'
                        }}
                      >
                        {resumes.map((file, index) => (
                          <ListItem 
                            key={index} 
                            secondaryAction={
                              <IconButton 
                                edge="end" 
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleRemoveResume(index);
                                }}
                                sx={{ color: '#dc143c', '&:hover': { bgcolor: 'rgba(220, 20, 60, 0.1)' } }}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            }
                            sx={{ borderBottom: '1px solid rgba(0,0,0,0.05)' }}
                          >
                            <ListItemText 
                              primary={file.name} 
                              secondary={`${(file.size / 1024).toFixed(1)} KB`} 
                              primaryTypographyProps={{ 
                                variant: 'body2', 
                                noWrap: true, 
                                sx: { maxWidth: '80%', color: '#3a3532', fontWeight: 500 } 
                              }}
                            />
                          </ListItem>
                        ))}
                      </List>
                    </Collapse>
                  </Box>
                )}
              </Box>

              <Box sx={{ flex: 1 }}>
                <Typography variant="subtitle1" gutterBottom sx={{ color: '#3a3532' }}>
                  Upload Job Description (PDF)
                </Typography>
                <DropzoneBox {...getJdRootProps()}>
                  <input {...getJdInputProps()} />
                  <UploadFileIcon sx={{ fontSize: 40, color: '#dc143c', mb: 2 }} />
                  <Typography sx={{ color: '#3a3532' }}>
                    {jd ? jd.name : 'Drag & drop JD here (PDF or DOCX), or click to select'}
                  </Typography>
                </DropzoneBox>
              </Box>
            </Box>

            <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
              <Button
                variant="contained"
                onClick={handleUpload}
                disabled={resumes.length === 0 || !jd || loading}
                startIcon={<AnalyticsIcon />}
                sx={{
                  backgroundColor: '#dc143c',
                  '&:hover': {
                    backgroundColor: '#b01030',
                  }
                }}
              >
                Analyze Files
              </Button>
              <Button
                variant="outlined"
                onClick={clearFiles}
                disabled={loading}
                startIcon={<DeleteIcon />}
                sx={{
                  color: '#dc143c',
                  borderColor: '#dc143c',
                  '&:hover': {
                    borderColor: '#dc143c',
                    backgroundColor: 'rgba(220, 20, 60, 0.04)',
                  }
                }}
              >
                Clear Files
              </Button>
            </Box>

            {loading && (
              <Box sx={{ width: '100%', mb: 3 }}>
                <LinearProgress 
                  variant="determinate" 
                  value={uploadProgress} 
                  sx={{
                    backgroundColor: 'rgba(220, 20, 60, 0.1)',
                    '& .MuiLinearProgress-bar': {
                      backgroundColor: '#dc143c',
                    }
                  }}
                />
                <Typography variant="body2" sx={{ mt: 1, color: '#3a3532' }}>
                  {uploadProgress}% Complete
                </Typography>
              </Box>
            )}

            {error && (
              <Alert 
                severity="error" 
                sx={{ 
                  mb: 3,
                  '& .MuiAlert-icon': {
                    color: '#dc143c',
                  }
                }}
              >
                {error}
              </Alert>
            )}
          </Paper>
        </Collapse>

        {results.length > 0 && (
          <>
            <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="h5" sx={{ color: '#3a3532', fontWeight: 600 }}>
                Analysis Results
              </Typography>
              <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                {selectedProfiles.length > 0 && (
                  <Typography variant="body2" sx={{ color: '#666', fontWeight: 600 }}>
                    {selectedProfiles.length} selected
                  </Typography>
                )}
                
                {/* Export Selected Data Button & Menu */}
                <Button
                  variant="outlined"
                  startIcon={<DownloadIcon />}
                  disabled={selectedProfiles.length === 0}
                  onClick={(e) => setAnchorElExportSelected(e.currentTarget)}
                  sx={{
                    color: '#dc143c',
                    borderColor: '#dc143c',
                    '&:hover': {
                      borderColor: '#dc143c',
                      backgroundColor: 'rgba(220, 20, 60, 0.04)',
                    },
                    '&.Mui-disabled': {
                      borderColor: 'rgba(0, 0, 0, 0.12)',
                    }
                  }}
                >
                  Export Selected
                </Button>
                <Menu
                  anchorEl={anchorElExportSelected}
                  open={Boolean(anchorElExportSelected)}
                  onClose={() => setAnchorElExportSelected(null)}
                >
                  <MenuItem onClick={() => { handleExport('pdf', true); setAnchorElExportSelected(null); }}>Export as PDF</MenuItem>
                  <MenuItem onClick={() => { handleExport('excel', true); setAnchorElExportSelected(null); }}>Export as Excel (.xlsx)</MenuItem>
                  <MenuItem onClick={() => { handleExport('csv', true); setAnchorElExportSelected(null); }}>Export as CSV</MenuItem>
                </Menu>

                {/* Export All Data Button & Menu */}
                <Button
                  variant="contained"
                  startIcon={<DownloadIcon />}
                  onClick={(e) => setAnchorElExportAll(e.currentTarget)}
                  sx={{
                    backgroundColor: '#dc143c',
                    '&:hover': {
                      backgroundColor: '#b01030',
                    }
                  }}
                >
                  Export All
                </Button>
                <Menu
                  anchorEl={anchorElExportAll}
                  open={Boolean(anchorElExportAll)}
                  onClose={() => setAnchorElExportAll(null)}
                >
                  <MenuItem onClick={() => { handleExport('pdf', false); setAnchorElExportAll(null); }}>Export as PDF</MenuItem>
                  <MenuItem onClick={() => { handleExport('excel', false); setAnchorElExportAll(null); }}>Export as Excel (.xlsx)</MenuItem>
                  <MenuItem onClick={() => { handleExport('csv', false); setAnchorElExportAll(null); }}>Export as CSV</MenuItem>
                </Menu>

                <Button
                  startIcon={showUpload ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                  onClick={() => setShowUpload(!showUpload)}
                  sx={{ color: '#dc143c', ml: 1 }}
                >
                  {showUpload ? 'Hide Upload' : 'Show Upload'}
                </Button>
              </Box>
            </Box>
            <TableContainer 
              component={Paper} 
              elevation={0}
              sx={{
                background: 'rgba(255, 255, 255, 0.8)',
                backdropFilter: 'blur(10px)',
                borderRadius: 2,
                border: '1px solid rgba(220, 20, 60, 0.1)',
                boxShadow: '0 8px 32px rgba(220, 20, 60, 0.1)',
              }}
            >
              <Table>
                <TableHead>
                  <TableRow>
                    <StyledTableCell padding="checkbox">
                      <Checkbox
                        sx={{
                          color: 'rgba(220, 20, 60, 0.5)',
                          '&.Mui-checked': { color: '#dc143c' },
                          '&.MuiCheckbox-indeterminate': { color: '#dc143c' }
                        }}
                        indeterminate={selectedProfiles.length > 0 && selectedProfiles.length < results.length}
                        checked={results.length > 0 && selectedProfiles.length === results.length}
                        onChange={handleSelectAllClick}
                      />
                    </StyledTableCell>
                    <StyledTableCell sx={{ minWidth: 200 }}>Candidate Name</StyledTableCell>
                    <StyledTableCell>Email</StyledTableCell>
                    <StyledTableCell>Job Role</StyledTableCell>
                    <StyledTableCell>Education</StyledTableCell>
                    <StyledTableCell>Skills</StyledTableCell>
                    <StyledTableCell>Experience</StyledTableCell>
                    <StyledTableCell>
                      Match Score
                      <Tooltip title="Calculated based on role match, skills, and experience">
                        <HelpOutlineIcon sx={{ ml: 1, fontSize: 16, verticalAlign: 'middle', opacity: 0.7 }} />
                      </Tooltip>
                    </StyledTableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {results.map((row, index) => {
                    const isItemSelected = isSelected(index);

                    return (
                      <StyledTableRow 
                        key={index}
                        onClick={(event) => handleClick(event, index)}
                        selected={isItemSelected}
                        sx={{ cursor: 'pointer' }}
                      >
                        <StyledTableCell padding="checkbox">
                          <Checkbox
                            sx={{
                              color: 'rgba(220, 20, 60, 0.5)',
                              '&.Mui-checked': { color: '#dc143c' }
                            }}
                            checked={isItemSelected}
                            onClick={(e) => e.stopPropagation()}
                            onChange={(e) => handleClick(e, index)}
                          />
                        </StyledTableCell>
                        <StyledTableCell>{row['Candidate Name'] || 'N/A'}</StyledTableCell>
                        <StyledTableCell>{row['Email'] || 'N/A'}</StyledTableCell>
                        <StyledTableCell>{row['Job Role'] || 'N/A'}</StyledTableCell>
                        <StyledTableCell>
                          {row['Education'] ? <EducationCell text={row['Education']} /> : 'N/A'}
                        </StyledTableCell>
                        <StyledTableCell>
                          {row['Skills'] ? <SkillCell text={row['Skills']} /> : 'N/A'}
                        </StyledTableCell>
                        <StyledTableCell>{row['Years of Experience'] || 'N/A'}</StyledTableCell>
                        <StyledTableCell>
                          <Tooltip title={getScoreText(row.similarity_score)}>
                            <Box 
                              sx={{ 
                                minWidth: 60,
                                height: 24,
                                px: 1,
                                bgcolor: getScoreColor(row.similarity_score).bg,
                                color: getScoreColor(row.similarity_score).text,
                                borderRadius: 1,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontSize: '0.875rem',
                                fontWeight: 600
                              }}
                            >
                              {formatScore(row.similarity_score)}
                            </Box>
                          </Tooltip>
                        </StyledTableCell>
                      </StyledTableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </>
        )}
      </Box>
      <Chatbot />
    </Box>
  );
};

export default UploadWizard;